class Sidebar extends HTMLElement {
  constructor() {
    super();
  }

  /**
   * Defines which triggers open the sidebar. Triggers must have the class "js-sidebar-open"
   * and a data attribute called target with the sidebar's ID.
   * Upon declaring a sidebar-component, it must have an ID, or it will not instantiate any method.
   *
   * @returns {void}
   */
  connectedCallback() {
    if (!this.id) return;
    this.openTriggers = document.querySelectorAll(
      `.js-sidebar-open[data-target="${this.id}"]`
    );
    this.sidebarOrientation = this.dataset.orientation;
    this.closeButtons = this.querySelectorAll(".js-sidebar-close");

    this.openTriggers?.forEach((trigger) => {
      trigger.addEventListener("click", this.open.bind(this));
    });

    this.closeButtons?.forEach((closeButton) => {
      closeButton.addEventListener("click", this.close.bind(this));
    });
  }

  /**
   * Method for opening the sidebar. It adds a class called "sidebar--show" to `this` element.
   * This sidebar implementation keeps a log of every other open sidebar as a data attribute
   * on the body DOM element, as well as the last open sidebar.
   * Upon opening a sidebar, it pushes its ID to the data attribute all-open-sidebar if it
   * is not there, closes temporalily the last open sidebar, and then registers `this.id` as
   * the last open sidebar on the body DOM element.
   * After this, it tries do target a "sidebar-overlay-component" and calls its method `show`
   * on it.
   *
   * @returns {void}
   */
  open() {
    this.classList.add(`sidebar--show`);
    const allOpenSidebars = (document.body.dataset.allOpenSidebar || "")
      .split(",")
      .filter(Boolean);

    if (!allOpenSidebars.includes(this.id)) {
      allOpenSidebars.push(this.id);
    }

    const prevLastId = document.body.dataset.lastOpenSidebar;
    if (prevLastId?.length > 0) {
      const prevLast = document.querySelector(`#${prevLastId}`);
      prevLast.classList.remove("sidebar--show");
    }

    document.body.dataset.allOpenSidebar = allOpenSidebars.join(",");
    document.body.dataset.lastOpenSidebar = this.id;

    document.querySelector("sidebar-overlay-component")?.show();
  }

  /**
   * Method for closing the sidebar. It removes a class called "sidebar--show" from `this`
   * element.
   * Upon removing, it filters `this.id` out of the data attribute `all-open-sidebar` on
   * the body, and registers the last item of its array as the last open sidebar, reopening
   * it to show its contents.
   * After this, if no other sidebar are open, it tries to target an element called
   * "sidebar-overlay-component" and calls its method `hide` on it.
   *
   * @returns {void}
   */
  close() {
    this.classList.remove(`sidebar--show`);
    const allOpenSidebar = (document.body.dataset.allOpenSidebar || "")
      .split(",")
      .filter(Boolean);

    const newOpenSidebar = allOpenSidebar.filter((id) => id !== this.id);
    document.body.dataset.allOpenSidebar = newOpenSidebar.join(",");

    const newLast = newOpenSidebar.at(-1) || "";
    document.body.dataset.lastOpenSidebar = newLast;
    if (!newLast) {
      document.querySelector("sidebar-overlay-component")?.hide();
    } else {
      document.querySelector(`#${newLast}`)?.classList.add("sidebar--show");
    }
  }
}

customElements.define("sidebar-component", Sidebar);

class SidebarOverlay extends HTMLElement {
  constructor() {
    super();
  }

  /**
   * Sets up an event listener for closing the last open sidebar-component upon clicking it
   *
   * @returns {void}
   */
  connectedCallback() {
    this.addEventListener("click", (e) => {
      e.stopPropagation();

      const openSidebarId = document.body.dataset.lastOpenSidebar;
      const currOpenSidebar = document.querySelector(`#${openSidebarId}`);

      currOpenSidebar?.close();
    });
  }

  /**
   * Adds a class called `show` to it.
   *
   * @returns {void}
   */
  show() {
    this.classList.add("show");
  }

  /**
   * Removes a class called `show` from it.
   *
   * @returns {void}
   */
  hide() {
    this.classList.remove("show");
  }
}

customElements.define("sidebar-overlay-component", SidebarOverlay);
